import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListPlus, Filter, X, Clock, Grid3x3, List } from 'lucide-react';
import { getTasks, deleteTask } from '../api/tasks';
import { getProjects } from '../api/projects';
import { getAllUsers } from '../api/users';
import { fetchMilestones } from '../api/milestones';
import { getTaskDependencies } from '../api/task-dependencies';
import { Task } from '../types/task';
import { Project } from '../types/project';
import { User } from '../types/user';
import { Milestone } from '../types/milestone';
import TaskForm from '../components/TaskForm';
import TaskTableView from '../components/TaskTableView';
import TaskCard from '../components/TaskCard';
import Button from '../components/Button';
import Select from '../components/Select';
import Breadcrumb from '../components/Breadcrumb';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';

const TasksPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isTenantViewer, hasNoActiveTenant } = useAuth();
    // Read-only members of the workspace cannot create, and neither can a session with no
    // workspace (backend additionally enforces edit permission on the selected project).
    const canCreateTasks = !!user && !isTenantViewer && !hasNoActiveTenant;
    const [tasks, setTasks] = useState<Task[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [milestones, setMilestones] = useState<Milestone[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [filterProject, setFilterProject] = useState<string>('');
    const [filterUser, setFilterUser] = useState<string>('');
    const [filterMilestone, setFilterMilestone] = useState<string>('');
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const [dependencyCounts, setDependencyCounts] = useState<Record<string, number>>({});
    const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
        const saved = localStorage.getItem('tasksView');
        return (saved === 'table' || saved === 'cards') ? saved : 'cards';
    });

    const loadTasks = async () => {
        try {
            setLoading(true);
            const [fetchedTasks, fetchedProjects, fetchedUsers, fetchedMilestones] = await Promise.all([
                getTasks(),
                getProjects(),
                getAllUsers(),
                fetchMilestones(),
            ]);
            setTasks(fetchedTasks);
            setProjects(fetchedProjects);
            setUsers(fetchedUsers);
            setMilestones(fetchedMilestones);
            setError(null);
        } catch (err) {
            setError('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTasks();
    }, []);

    // Handle query parameters for project pre-selection and form auto-open
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const projectId = params.get('project');
        const shouldAdd = params.get('add') === 'true';

        if (projectId) {
            setFilterProject(projectId);
        }
        if (shouldAdd) {
            setShowForm(true);
        }

        // Clean URL after reading params
        if (projectId || shouldAdd) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    // Persist view mode to localStorage
    useEffect(() => {
        localStorage.setItem('tasksView', viewMode);
    }, [viewMode]);

    // Load dependency counts for all tasks
    useEffect(() => {
        const loadDependencyCounts = async () => {
            const counts: Record<string, number> = {};
            for (const task of tasks) {
                try {
                    const deps = await getTaskDependencies(task.id);
                    counts[task.id] = deps.length;
                } catch (err) {
                    // If error loading dependencies, set count to 0
                    counts[task.id] = 0;
                }
            }
            setDependencyCounts(counts);
        };
        if (tasks.length > 0) {
            loadDependencyCounts();
        }
    }, [tasks]);

    // Handle navigation state from dashboard/notifications
    useEffect(() => {
        const state = location.state as { 
            filterType?: string; 
            filterUserId?: string;
            projectId?: string;
            milestoneId?: string;
            autoApply?: boolean;
        } | null;
        
        if (state?.filterType && state?.autoApply) {
            setActiveFilter(state.filterType);
            
            // Auto-set filters based on filter type
            if (state.filterType === 'user' && state.filterUserId) {
                setFilterUser(state.filterUserId);
            } else if (state.filterType === 'project' && state.projectId) {
                setFilterProject(state.projectId);
            } else if (state.filterType === 'milestone' && state.milestoneId) {
                setFilterMilestone(state.milestoneId);
            }
        }
    }, [location.state]);

    const handleSuccess = (updatedTask: Task) => {
        if (editingTask) {
            setTasks(tasks.map(t => t.id === updatedTask.id ? updatedTask : t));
            setEditingTask(undefined);
            showToast.success('Task updated successfully!');
        } else {
            setTasks([...tasks, updatedTask]);
            showToast.success('Task created successfully!');
        }
        setShowForm(false);
    };

    const handleEdit = (task: Task) => {
        setEditingTask(task);
        setShowForm(true);
    };

    const handleDelete = async (taskId: string) => {
        try {
            await deleteTask(String(taskId));
            setTasks(tasks.filter(t => t.id !== taskId));
            setDeleteConfirm(null);
            showToast.success('Task deleted successfully!');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete task';
            setError(errorMessage);
            showToast.error(errorMessage);
        }
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingTask(undefined);
    };

    const getProjectName = (projectId: string): string => {
        if (!projects || !Array.isArray(projects)) return `Project #${projectId}`;
        const project = projects.find(p => p.id === projectId);
        return project ? project.name : `Project #${projectId}`;
    };

    const getUserName = (userId: string): string => {
        const user = users.find(u => u.id === userId);
        return user ? (user.full_name || user.username) : `User #${userId}`;
    };

    const getMilestoneName = (milestoneId: string | undefined): string | null => {
        if (!milestoneId) return null;
        const milestone = milestones.find(m => m.id === milestoneId);
        return milestone ? milestone.name : `Milestone #${milestoneId}`;
    };

    // Filter tasks based on selected project, user, milestone, and active filter
    const filteredTasks = tasks.filter(task => {
        const matchesProject = !filterProject || task.project_id === filterProject;
        const matchesUser = !filterUser || (task.assigned_to && task.assigned_to === filterUser);
        
        // Handle milestone filter including "No Milestone" option
        let matchesMilestone = true;
        if (filterMilestone === 'none') {
            matchesMilestone = !task.milestone_id;
        } else if (filterMilestone) {
            matchesMilestone = task.milestone_id === filterMilestone;
        }
        
        // Apply active filter from navigation
        if (activeFilter === 'overdue') {
            const isOverdue = task.due_date && 
                new Date(task.due_date) < new Date() && 
                task.status !== 'completed';
            return matchesProject && matchesUser && matchesMilestone && isOverdue;
        }
        
        if (activeFilter === 'upcoming') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const sevenDaysFromNow = new Date(today);
            sevenDaysFromNow.setDate(today.getDate() + 7);
            
            const isUpcoming = task.due_date &&
                new Date(task.due_date) >= today &&
                new Date(task.due_date) <= sevenDaysFromNow &&
                task.status !== 'completed';
            return matchesProject && matchesUser && matchesMilestone && isUpcoming;
        }
        
        if (activeFilter === 'unassigned') {
            const isUnassigned = !task.assigned_to || task.assigned_to === null;
            return matchesProject && matchesUser && matchesMilestone && isUnassigned;
        }
        
        return matchesProject && matchesUser && matchesMilestone;
    });

    if (loading) {
        return (
            <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
                <div className="flex justify-between items-center mb-8">
                    <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                    <div className="flex gap-3">
                        <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                        <div className="h-10 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
            {/* Breadcrumb - show based on active filters */}
            {(filterProject || filterMilestone) && (
                <Breadcrumb
                    items={[
                        ...(filterProject ? [
                            { label: 'Projects', path: '/projects' },
                            { label: projects.find(p => p.id === filterProject)?.name || 'Project', path: '/projects' },
                        ] : []),
                        ...(filterMilestone ? [
                            { label: 'Milestones', path: '/milestones' },
                            { label: milestones.find(m => m.id === filterMilestone)?.name || 'Milestone', path: '/milestones' },
                        ] : []),
                        { label: 'Tasks' }
                    ]}
                />
            )}
            
            <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Tasks</h1>
                <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
                    {/* View Toggle */}
                    <div className="flex gap-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-lg p-1">
                        <Button
                            variant={viewMode === 'cards' ? 'primary' : 'outline'}
                            size="sm"
                            leftIcon={Grid3x3}
                            onClick={() => setViewMode('cards')}
                            title="Card View"
                        >
                            Cards
                        </Button>
                        <Button
                            variant={viewMode === 'table' ? 'primary' : 'outline'}
                            size="sm"
                            leftIcon={List}
                            onClick={() => setViewMode('table')}
                            title="Table View"
                        >
                            Table
                        </Button>
                    </div>
                    <Button 
                        onClick={loadTasks} 
                        variant="outline" 
                        disabled={loading}
                        leftIcon={Clock}
                    >
                        Refresh
                    </Button>
                    {canCreateTasks && (
                        <Button
                            onClick={() => setShowForm(!showForm)}
                            variant="primary"
                            leftIcon={showForm ? X : ListPlus}
                        >
                            {showForm ? 'Hide Form' : 'Add New Task'}
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <div className="mb-6 rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
                    {error}
                </div>
            )}

            {/* Active Filter Indicator */}
            {(activeFilter || filterProject || filterUser || filterMilestone) && (
                <div className="bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500 dark:border-primary-700 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Filter className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            <div className="flex flex-col gap-1">
                                {activeFilter && (
                                    <span className="font-semibold text-primary-900 dark:text-primary-100">
                                        Active Filter: {activeFilter === 'overdue' ? 'Overdue Tasks' :
                                                      activeFilter === 'upcoming' ? 'Upcoming Tasks (Next 7 Days)' :
                                                      'Unassigned Tasks'}
                                        {filterUser && user && filterUser === user.id && (
                                            <span className="ml-2 text-primary-700 dark:text-primary-300 font-normal">(Your Tasks)</span>
                                        )}
                                    </span>
                                )}
                                {filterProject && (
                                    <span className="font-semibold text-primary-900 dark:text-primary-100">
                                        Project: {projects.find(p => p.id === filterProject)?.name || 'Unknown'}
                                    </span>
                                )}
                                {filterMilestone && (
                                    <span className="font-semibold text-primary-900 dark:text-primary-100">
                                        Milestone: {milestones.find(m => m.id === filterMilestone)?.name || 'Unknown'}
                                    </span>
                                )}
                                {filterUser && !activeFilter && (
                                    <span className="font-semibold text-primary-900 dark:text-primary-100">
                                        User: {users.find(u => u.id === filterUser)?.full_name || users.find(u => u.id === filterUser)?.username || 'Unknown'}
                                    </span>
                                )}
                            </div>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm"
                            leftIcon={X} 
                            onClick={() => {
                                setActiveFilter(null);
                                setFilterProject('');
                                setFilterUser('');
                                setFilterMilestone('');
                            }}
                        >
                            Clear All Filters
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex gap-4 items-end p-6 bg-white dark:bg-gray-800 rounded-xl mb-8 border border-gray-200 dark:border-gray-700 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <Select
                        label="Project"
                        value={filterProject}
                        onChange={(e) => setFilterProject(e.target.value)}
                    >
                        <option value="">All Projects</option>
                        {projects.map(project => (
                            <option key={project.id} value={project.id}>
                                {project.name}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                    <Select
                        label="User"
                        value={filterUser}
                        onChange={(e) => setFilterUser(e.target.value)}
                    >
                        <option value="">All Users</option>
                        {users.map(user => (
                            <option key={user.id} value={user.id}>
                                {user.full_name || user.username}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                    <Select
                        label="Milestone"
                        value={filterMilestone}
                        onChange={(e) => setFilterMilestone(e.target.value)}
                    >
                        <option value="">All Milestones</option>
                        <option value="none">No Milestone</option>
                        {milestones.map(milestone => (
                            <option key={milestone.id} value={milestone.id}>
                                {milestone.name}
                            </option>
                        ))}
                    </Select>
                </div>

                {(filterProject || filterUser || filterMilestone) && (
                    <Button
                        onClick={() => { setFilterProject(''); setFilterUser(''); setFilterMilestone(''); }}
                        variant="outline"
                        size="sm"
                    >
                        Clear Filters
                    </Button>
                )}
            </div>

            {showForm && (
                <div className="mb-8">
                    <TaskForm
                        task={editingTask}
                        onSuccess={handleSuccess}
                        onCancel={handleCancelForm}
                    />
                </div>
            )}

            {filteredTasks.length === 0 ? (
                <div className="text-center p-12 text-gray-500 dark:text-gray-400">
                    {tasks.length === 0
                        ? 'No tasks found. Create your first task above!'
                        : 'No tasks match the selected filters.'}
                </div>
            ) : viewMode === 'table' ? (
                <TaskTableView
                    tasks={filteredTasks}
                    projects={projects}
                    users={users}
                    milestones={milestones}
                    onView={(taskId) => navigate(`/tasks/${taskId}`)}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredTasks.map(task => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            deleteConfirm={deleteConfirm}
                            dependencyCount={dependencyCounts[task.id] || 0}
                            onView={(t) => navigate(`/tasks/${t.id}`)}
                            onEdit={handleEdit}
                            onDeleteConfirm={setDeleteConfirm}
                            onDeleteCancel={() => setDeleteConfirm(null)}
                            onDelete={handleDelete}
                            getProjectName={getProjectName}
                            getMilestoneName={getMilestoneName}
                            getUserName={getUserName}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default TasksPage;